import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import css from './TnaDetails.module.css';

import toShort from '../../../../../services/utils/dateParser';
import {
    durationToMinutes,
    minutesToDuration,
    getAtaDuration,
    getAtaLevel,
} from '../../../../../services/utils/tna.js';
import * as tnaServices from '../../../../../services/tnaServices';

import { machineOptions } from '../configuration';

import TnaGeneral from './TnaGeneral';
import TnaGeneralEdit from './TnaGeneralEdit';
import TnaModSelect from './TnaModSelect';
import FormButtons from '../../../_common/elements/forms/FormButtons';
import TnaEditQuestions from './TnaEditQuestions';
import GenerateTnaDocument from '../generate/GenerateTnaDocument';

function TnaDetails() {
    const [isLoading, setIsLoading] = useState(false);
    const { tnaId } = useParams();
    const [tnaDetails, setTnaDetails] = useState({});
    const [enhancedTnaDetails, setEnhancedTnaDetails] = useState(null);
    const [allAtas, setAllAtas] = useState([]);
    const [totalTnaDuration, setTotalTnaDuration] = useState(0); // Total duration for the whole TNA
    const [totalMcq, setTotalMcq] = useState(0); // Total duration for the whole TNA
    const [machine, setMachine] = useState(machineOptions);
    const [modal, setModal] = useState(false);
    const [selectMods, setSelectMods] = useState(false);
    const [editAtaQuestions, setEditAtaQuestions] = useState(0);

    const navigate = useNavigate();

    // Fetch TNA details and ATAs on component mount
    useEffect(() => {
        if (!selectMods && !modal && !editAtaQuestions) {
            tnaServices
                .getById(tnaId)
                .then((res) => {
                    setTnaDetails(res.data[0] || {});
                })
                .catch((err) => {
                    console.error('Error fetching TNA details:', err);
                });
        }
    }, [tnaId, modal, selectMods, editAtaQuestions]);

    // Fetch all ATAs on component mount
    useEffect(() => {
        tnaServices
            .getOption('getAllAtas')
            .then((res) => {
                setAllAtas(res.data || []);
            })
            .catch((err) => {
                console.error('Error fetching ATAs:', err);
            });
    }, []);

    useEffect(() => {
        if (tnaDetails?.modules?.length > 0 && !isLoading) {
            setIsLoading(true);
            const enhanceModules = async () => {
                try {
                    const enhancedModules = await Promise.all(
                        tnaDetails.modules.map(async (module) => {
                            try {
                                const res = await tnaServices.getModuleById(module.tnaModuleId);
                                const moduleData = Array.isArray(res.data) ? res.data[0] : res.data;

                                return {
                                    ...module,
                                    tnaModuleTitle: moduleData?.tnaModuleTitle || 'No Title',
                                    duration: moduleData?.totalDuration || '00:00',
                                    level: moduleData?.level || 'No Level',
                                    ata: moduleData?.ata || [],
                                };
                            } catch (err) {
                                console.error('Error fetching module details:', err);
                                return module;
                            }
                        })
                    );

                    const updatedTnaDetails = {
                        ...tnaDetails,
                        modules: enhancedModules,
                    };

                    setEnhancedTnaDetails(updatedTnaDetails);

                    // Calculate total TNA duration
                    calculateTotalTnaDuration(enhancedModules);

                    // Calculate total MCQ
                    calculateTotalMcq(tnaDetails.mcq);
                } catch (error) {
                    console.error('Error in enhanceModules:', error);
                } finally {
                    setIsLoading(false);
                }
            };

            enhanceModules();
        } else if (tnaDetails && !tnaDetails.modules) {
            setEnhancedTnaDetails(tnaDetails);
            setIsLoading(false);
        }
    }, [tnaDetails]);

    // Calculate total TNA duration
    const calculateTotalTnaDuration = (modules) => {
        if (!modules || modules.length === 0) {
            setTotalTnaDuration(0);
            return;
        }

        const total = modules.reduce((sum, module) => {
            // const moduleDuration = module.duration || '00:02';
            const moduleDuration = module.ata?.reduce((ataSum, ata) => {
                const ataDuration = ata.duration || '00:00';
                const [ataHours, ataMinutes] = ataDuration.split(':').map(Number);
                return ataSum + (ataHours * 60 + ataMinutes);
            }, 0);
            // console.log(moduleDuration);

            // const [hours, minutes] = moduleDuration.split(':').map(Number);
            // return sum + (hours * 60 + minutes);
            return sum + moduleDuration;
        }, 0);

        setTotalTnaDuration(total);
    };
    const calculateTotalMcq = (tnaMcq) => {
        if (!tnaMcq || tnaMcq.length === 0) {
            setTotalMcq(0);
            return;
        }

        const total = tnaMcq.reduce((sum, tnaMcq) => {
            // const moduleDuration = module.duration || '00:02';
            const mcq = tnaMcq.mcqQty || 0;

            return sum + mcq;
        }, 0);

        setTotalMcq(total);
    };

    // Calculate total duration for each ATA chapter across all modules
    const getTotalAtaDuration = (ataId) => {
        if (!enhancedTnaDetails?.modules) return '00:00';

        const totalMinutes = enhancedTnaDetails.modules.reduce((sum, module) => {
            const duration = getAtaDuration(module, ataId);
            return sum + durationToMinutes(duration);
        }, 0);

        return minutesToDuration(totalMinutes);
    };

    // Get maximum level for each module (highest level among all ATAs in the module)
    const getModuleMaxLevel = (ataId) => {
        if (!enhancedTnaDetails?.modules) return 0;
        const levels = enhancedTnaDetails?.modules.map((module) => {
            const ataInModule = module.ata.find((el) => el.ataId === ataId);
            return ataInModule ? Number(ataInModule.level) || 0 : 0;
        });
        return Math.max(...levels);
    };

    // Filter ATAs based on machine state
    const filteredAtas = useMemo(() => {
        return allAtas.filter((ata) => machine[ata.ataPart]);
    }, [machine, allAtas]);

    const filterOnChange = (e) => {
        setMachine((prev) => ({
            ...prev,
            [e.target.name]: e.target.checked,
        }));
    };

    // to be used for adding questions later
    const handleEditQuestions = (ata) => {
        // Your edit logic here
        setEditAtaQuestions(ata.ataId);
    };

    const toggleModal = () => {
        setModal(!modal);
    };

    // if (isLoading) {
    //     return <div>Loading module details...</div>;
    // }

    const handleModButton = () => {
        setSelectMods(!selectMods);
    };

    const handleLockButton = () => {
        // Are you sure you want to lock the TNA?
        const confirmLock = window.confirm(
            `Are you sure you want to lock the TNA "${lockData.tnaTitle}"? This action cannot be undone.`
        );
        if (!confirmLock) return;
        // Lock TNA logic here
        try {
            tnaServices
                .lockTna(lockData.tnaId, lockData)
                .then((res) => {
                    console.log('TNA locked successfully:', res);
                    alert(`TNA "${lockData.tnaTitle}" has been locked successfully.`);
                })
                .catch((err) => {
                    console.error('Error locking TNA:', err);
                    alert(`Failed to lock TNA "${lockData.tnaTitle}". Please try again later.`);
                });
        } catch (error) {
            alert(`Failed to lock TNA "${lockData.tnaTitle}". Please try again later.`);
        }

        // }
    };

    const lockData = {
        tnaId: tnaDetails.tnaId,
        tnaTitle: tnaDetails.tnaTitle,
        tnaTotalDurationMin: totalTnaDuration,
        tnaTotalMcq: totalMcq,
        tnaData: prepareDataForJsonStorage(enhancedTnaDetails),
        handleLockButton: handleLockButton,
    };

    function prepareDataForJsonStorage(data) {
        // First, ensure it's a clean object
        const cleanData = JSON.parse(JSON.stringify(data));

        // Function to recursively clean string values
        function cleanStrings(obj) {
            if (typeof obj === 'string') {
                // Escape characters that need escaping in JSON strings
                return obj
                    .replace(/\\/g, '\\\\') // Escape backslashes first
                    .replace(/\"/g, '\\"') // Escape double quotes
                    .replace(/\n/g, '\\n') // Escape newlines
                    .replace(/\r/g, '\\r') // Escape carriage returns
                    .replace(/\t/g, '\\t') // Escape tabs
                    .replace(/\f/g, '\\f'); // Escape form feeds
                // .replace(/\b/g, '\\b');  // Escape backspaces
            }

            if (Array.isArray(obj)) {
                return obj.map(cleanStrings);
            }

            if (obj && typeof obj === 'object') {
                const cleaned = {};
                for (const [key, value] of Object.entries(obj)) {
                    cleaned[key] = cleanStrings(value);
                }
                return cleaned;
            }

            return obj;
        }

        const finalData = cleanStrings(cleanData);

        // Now stringify - this should work perfectly
        try {
            const jsonString = JSON.stringify(finalData);
            // Verify it's valid
            JSON.parse(jsonString);
            return jsonString;
        } catch (error) {
            console.error('Failed to create valid JSON:', error);
            throw new Error('Data contains invalid JSON characters');
        }
    }

    return (
        <>
            <h2>{tnaDetails.tnaTitle}</h2>
            <TnaGeneral
                tnaDetails={tnaDetails}
                totalDuration={totalTnaDuration}
                toggleModal={toggleModal}
                minutesToDuration={minutesToDuration}
                totalMcq={totalMcq}
                lockData={lockData}
            />

            <div className={css.course}>
                <h2 className={css.h2}>Details</h2>
                <div className={css.filters}>
                    {Object.keys(machine).map((x) => (
                        <div key={x}>
                            <input type="checkbox" name={x} id={x} checked={machine[x]} onChange={filterOnChange} />
                            <label htmlFor={x} className={css.label}>
                                {x}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className={css.functionalButtons}>
                <button onClick={handleModButton} className={css.selectButton}>
                    Select modules
                </button>
                <GenerateTnaDocument />
            </div>

            <table className={css.table}>
                <thead className={css.thead}>
                    <tr>
                        <th className={`${css.th} ${css.center}`}>ATA</th>
                        <th className={css.th}>Title</th>
                        {enhancedTnaDetails?.modules?.map((mod, index) => (
                            <th key={index} className={`${css.th} ${css.center}`}>
                                <div
                                    onClick={() => navigate(`/tna/modules/details/${mod.tnaModuleId}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {mod.tnaModuleTitle || `Module ${index + 1}`}
                                </div>
                                {/* <div className={css.subHeader}>
                                    Level: {getModuleMaxLevel(mod)}
                                </div> */}
                            </th>
                        ))}
                        <th className={`${css.th} ${css.center}`}>Total Duration</th>
                        <th className={`${css.th} ${css.center}`}>Level</th>
                        <th className={`${css.th} ${css.center}`}>MCQ</th>
                        <th className={`${css.th} ${css.center}`}>Actions</th>
                    </tr>
                </thead>
                <tbody className={css.tbody}>
                    {filteredAtas.length > 0 ? (
                        filteredAtas.map((ata) => {
                            const totalAtaDuration = getTotalAtaDuration(ata.ataId);
                            return (
                                <tr key={ata.ataId}>
                                    <td className={css.center}>{ata.ataNo}</td>
                                    <td className={css.left}>{ata.ataTitle}</td>
                                    {enhancedTnaDetails?.modules?.map((mod, modIndex) => (
                                        <td
                                            key={modIndex}
                                            className={css.center}
                                            style={
                                                getAtaDuration(mod, ata.ataId).slice(0, 5) === '00:00'
                                                    ? { backgroundColor: '#f8d7da' }
                                                    : {}
                                            }
                                        >
                                            {getAtaDuration(mod, ata.ataId).slice(0, 5)}{' '}
                                            <span style={!getAtaLevel(mod, ata.ataId) ? { color: 'red' } : {}}>
                                                ({getAtaLevel(mod, ata.ataId)})
                                            </span>
                                        </td>
                                    ))}
                                    <td
                                        className={`${css.center} ${css.totalDurationCell}`}
                                        style={totalAtaDuration === '00:00' ? { color: 'red' } : {}}
                                    >
                                        <strong>{totalAtaDuration}</strong>
                                    </td>
                                    <td
                                        className={`${css.center} ${css.totalDurationCell}`}
                                        style={totalAtaDuration === '00:00' ? { color: 'red' } : {}}
                                    >
                                        <strong>{getModuleMaxLevel(ata.ataId)}</strong>
                                    </td>
                                    <td
                                        className={`${css.center} ${css.totalDurationCell}`}
                                        style={
                                            tnaDetails?.mcq?.find(
                                                (x) => x.ataId === ata.ataId && x.tnaId === tnaDetails?.tnaId
                                            )?.mcqQty
                                                ? {}
                                                : { backgroundColor: '#f8d7da' }
                                        }
                                    >
                                        <strong>
                                            {
                                                tnaDetails?.mcq?.find(
                                                    (x) => x.ataId === ata.ataId && x.tnaId === tnaDetails?.tnaId
                                                )?.mcqQty
                                            }
                                        </strong>
                                    </td>
                                    <td className={css.center}>
                                        <button className={css.editButton} onClick={() => handleEditQuestions(ata)}>
                                            <i className={`fa-solid fa-pen ${css.orange}`}> </i>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={4 + (enhancedTnaDetails?.modules?.length || 0)}
                                style={{ textAlign: 'center' }}
                            >
                                No matching records found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {modal && <TnaGeneralEdit tnaData={tnaDetails} toggleModal={toggleModal} method={'edit'} onAdd={null} />}
            {selectMods && <TnaModSelect tnaDetails={tnaDetails} toggleModal={handleModButton} method={'select'} />}
            {editAtaQuestions && (
                <TnaEditQuestions
                    setEditAtaQuestions={setEditAtaQuestions}
                    ataId={editAtaQuestions}
                    tnaDetails={tnaDetails}
                />
            )}
        </>
    );
}

export default TnaDetails;
